import type { UserTask } from "@/types/userTasks.ts";
import { dueDeadlineMs } from "./userTaskDue.ts";

/**
 * When a task the reader committed to comes back.
 *
 * The choices are days rather than times, because a commitment is "I will get
 * to this by then" and not an appointment.
 *
 * A calendar, and nothing fixed beside it. There were three fixed choices —
 * Later today, Tomorrow and Next week — and a menu of four asked the reader to
 * weigh options for a question most of them answer with "not now". Every one
 * of them was a date the calendar can give, except Later today, so a
 * commitment to clear something before stopping is the one thing this no
 * longer says.
 *
 * The sweep that wakes a task runs hourly, so a return time is honoured to
 * within the hour. Anything finer would be a promise the server does not keep.
 */

/** 9am: early enough to be the day's work, late enough not to be the night's. */
const MORNING_HOUR = 9;

/**
 * The row that opens the calendar, in the reader's own voice: not a day being
 * named but them saying when they will do the thing, which is what a
 * commitment is. No fixed "tomorrow" beside it: tomorrow is a day like any
 * other, and Snooze already puts a task down until then in one tap.
 */
export const COMMIT_BY = "I'll do it by…";

/** 9am on the given day, in the reader's own time. */
export function commitOnDay(day: Date): number {
  const date = new Date(day);
  date.setHours(MORNING_HOUR, 0, 0, 0);
  return date.getTime();
}

/** The first and, where the task is due, the last day a commitment may name. */
export type CommitDays = { first: Date; last?: Date };

/**
 * The days a commitment may name.
 *
 * From tomorrow. Today's morning has usually gone, and a task set for a time
 * already past wakes at once, which reads as the commitment having failed
 * before it was made. Up to the day the task is due, where that is still
 * ahead: promising to do it after it was due is not a commitment. Open-ended
 * where it is already late, as a snooze is (BIG-9170) — the reader knows.
 *
 * Null when no day fits: the task is due before tomorrow morning. The menus
 * offer nothing then, and Done beside them is the answer.
 */
export function commitDays(
  task: Pick<UserTask, "dueMs" | "dueDate">,
  nowMs: number = Date.now(),
): CommitDays | null {
  const first = new Date(nowMs);
  first.setDate(first.getDate() + 1);
  first.setHours(0, 0, 0, 0);
  const deadline = dueDeadlineMs(task);
  if (deadline === null || deadline <= nowMs) return { first };
  // The last day whose morning still comes before the task is due.
  const last = new Date(deadline);
  last.setHours(0, 0, 0, 0);
  if (commitOnDay(last) > deadline) last.setDate(last.getDate() - 1);
  return last < first ? null : { first, last };
}
